import CardTheMagicianIImg from "@/assets/img/today/tarot/cards/card_the_magician_i__2x.webp"
import CardTheHierophantVImg from "@/assets/img/today/tarot/cards/card_the_hierophant_v__2x.webp"
import CardTheEmperorIvImg from "@/assets/img/today/tarot/cards/card_the_emperor_iv__2x.webp"
import CardDeathXiiiImg from "@/assets/img/today/tarot/cards/card_death_xiii__2x.webp"
import CardTheHermitIxImg from "@/assets/img/today/tarot/cards/card_the_hermit_ix__2x.webp"
import CardTheDevilXvImg from "@/assets/img/today/tarot/cards/card_the_devil_xv__2x.webp"
import CardTemperanceXivImg from "@/assets/img/today/tarot/cards/card_temperance_xiv__2x.webp"
import CardTheFool0Img from "@/assets/img/today/tarot/cards/card_the_fool_0__2x.webp"
import CardTheChariotViiImg from "@/assets/img/today/tarot/cards/card_the_chariot_vii__2x.webp"
import CardJusticeXiImg from "@/assets/img/today/tarot/cards/card_justice_xi__2x.webp"
import CardJudgementXxImg from "@/assets/img/today/tarot/cards/card_judgement_xx__2x.webp"
import CardStrengthViiiImg from "@/assets/img/today/tarot/cards/card_strength_viii__2x.webp"
import CardTheEmpressIiiImg from "@/assets/img/today/tarot/cards/card_the_empress_iii__2x.webp"
import CardWheelOfFortuneXImg from "@/assets/img/today/tarot/cards/card_wheel_of_fortune_x__2x.webp"
import CardTheStarXviiImg from "@/assets/img/today/tarot/cards/card_the_star_xvii__2x.webp"
import CardTheMoonXviiiImg from "@/assets/img/today/tarot/cards/card_the_moon_xviii__2x.webp"
import CardTheLoversViImg from "@/assets/img/today/tarot/cards/card_the_lovers_vi__2x.webp"
import CardTheSunXixImg from "@/assets/img/today/tarot/cards/card_the_sun_xix__2x.webp"
import CardTheHangedManXiiImg from "@/assets/img/today/tarot/cards/card_the_hanged_man_xii__2x.webp"
import CardTheTowerXviImg from "@/assets/img/today/tarot/cards/card_the_tower_xvi__2x.webp"
import CardTheWorldXxiImg from "@/assets/img/today/tarot/cards/card_the_world_xxi__2x.webp"
import CardTheHighPriestessIiImg from "@/assets/img/today/tarot/cards/card_the_high_priestess_ii__2x.webp"

/** Tarot card content, looked up by the astro-today daily card `name`. */
export interface TarotCard {
  roman: string
  name: string
  image: string
  description: string
}

export const TAROT_CARDS: TarotCard[] = [
  {
    roman: "I",
    name: "The Magician",
    image: CardTheMagicianIImg,
    description:
      "You have what you need today. The Magician points to the resources, skills, and timing already in your hands - the question is whether you'll use them. This isn't a day for wishful thinking. It's a day for action. Pick the one thing that matters and pour yourself into it. Magic, in the tarot sense, is just focused intention.",
  },
  {
    roman: "V",
    name: "The Hierophant",
    image: CardTheHierophantVImg,
    description:
      "The Hierophant brings questions about belonging - to a tradition, a teacher, a way of doing things. Maybe you're being asked to follow rules you didn't make; maybe you're being asked to teach. Either way, look at what you've absorbed from family, culture, religion. What still serves you? What's just inherited weight?",
  },
  {
    roman: "IV",
    name: "The Emperor",
    image: CardTheEmperorIvImg,
    description:
      "Today rewards order. The Emperor shows up when something in your life needs structure - boundaries, rules, a plan, an actual schedule. Where have you been avoiding the boring foundational work? That's where today's energy lives. Sit down, make the list, take charge of the decision no one else will.",
  },
  {
    roman: "XIII",
    name: "Death",
    image: CardDeathXiiiImg,
    description:
      "Death is the most misunderstood card in the deck. It almost never means literal death - it means ending. Something is finishing in your life: a phase, a belief, a relationship, a version of yourself. Don't resist it. The card is also a promise: what ends now makes room for what's been waiting to begin.",
  },
  {
    roman: "IX",
    name: "The Hermit",
    image: CardTheHermitIxImg,
    description:
      "Pull back today. The Hermit asks for solitude, not as withdrawal but as sacred work. Whatever you've been processing needs silence to integrate. Cancel what you can. Walk alone. Sit with the question that's been chasing you. The answer arrives when you stop looking for it everywhere except inside.",
  },
  {
    roman: "XV",
    name: "The Devil",
    image: CardTheDevilXvImg,
    description:
      "The Devil shows up when you're stuck in a pattern you'd rather not look at - over-drinking, over-working, over-pleasing, over-thinking. The chains in the card are loose. You're not actually trapped. You're just used to wearing them. Today asks: which one is ready to come off?",
  },
  {
    roman: "XIV",
    name: "Temperance",
    image: CardTemperanceXivImg,
    description:
      "Temperance is the alchemist - slowly combining elements until they make something new. Today is not for big moves; it's for refinement. Mix the right amount of effort and rest, hope and realism, expression and silence. Patience here pays dividends that bigger gestures couldn't.",
  },
  {
    roman: "0",
    name: "The Fool",
    image: CardTheFool0Img,
    description:
      "The Fool shows up when something fresh is calling - a project, a relationship, an idea you've been afraid to start. The card asks for trust, not certainty. You won't have all the answers before you begin, and that's the whole point. Step forward anyway. The path makes itself clear once you're walking it.",
  },
  {
    roman: "VII",
    name: "The Chariot",
    image: CardTheChariotViiImg,
    description:
      "Push forward. The Chariot is the rare card of pure forward momentum - when everything you've been training for converges into one moment of action. Don't second-guess. Don't poll your friends. Take the reins, point in the direction you want to go, and move. Hesitation today costs more than mistakes.",
  },
  {
    roman: "XI",
    name: "Justice",
    image: CardJusticeXiImg,
    description:
      "Justice asks for honesty today - with others, but mostly with yourself. Something is out of balance and needs your acknowledgment, not your spin. The truth doesn't have to be brutal, but it does have to be said. Whatever you've been rationalizing this week - that's where today's work is.",
  },
  {
    roman: "XX",
    name: "The Judgment",
    image: CardJudgementXxImg,
    description:
      "The Judgment is the moment of reckoning - not external judgment, but the inner voice that asks: have you been living the life that's actually yours? Today brings clarity about a chapter that's complete and a calling that's starting. Listen for the question you've been avoiding. Then answer it.",
  },
  {
    roman: "VIII",
    name: "Strength",
    image: CardStrengthViiiImg,
    description:
      "Strength in tarot isn't muscle - it's the woman calmly placing her hand on the lion's mouth. Today asks you to face something that scares you, but with grace instead of force. Anger won't move the needle. Patience and quiet conviction will. You're more powerful than you've been letting yourself feel.",
  },
  {
    roman: "III",
    name: "The Empress",
    image: CardTheEmpressIiiImg,
    description:
      "Soften today. The Empress brings the energy of growth, beauty, and care - for yourself and for what you're creating. If you've been pushing hard, this is the card asking you to receive. Tend to your body, your space, your people. What you nurture now becomes what blooms next season.",
  },
  {
    roman: "X",
    name: "The Wheel of Fortune",
    image: CardWheelOfFortuneXImg,
    description:
      "The wheel turns today. Something that's been stuck moves; something that's been moving stops. You can't control the timing, but you can choose how you meet it. If life's been hard, the shift is coming. If it's been easy, savor what's here. The only constant is change - the only response is presence.",
  },
  {
    roman: "XVII",
    name: "The Star",
    image: CardTheStarXviiImg,
    description:
      "After difficulty, the Star arrives. Today brings the first signal of healing - quiet, not loud. Don't rush back to busyness. Sit in the relief. Let yourself believe something good can happen again. The card reminds you that the night was the necessary part of seeing the stars at all.",
  },
  {
    roman: "XVIII",
    name: "The Moon",
    image: CardTheMoonXviiiImg,
    description:
      "The Moon clouds the path today. Things aren't what they seem - your fears might be amplified, your perceptions distorted. Don't make big decisions in this light. Trust your dreams and intuition more than your reasoning. The fog lifts; in the meantime, walk slowly and don't trust every shadow.",
  },
  {
    roman: "VI",
    name: "The Lovers",
    image: CardTheLoversViImg,
    description:
      "The Lovers is about more than romance - it's about choosing what aligns with your values. Today, a decision needs to be made between two paths, and the right one isn't necessarily the easier one. Listen to what your gut says about who and what you give your time to. Alignment over convenience.",
  },
  {
    roman: "XIX",
    name: "The Sun",
    image: CardTheSunXixImg,
    description:
      "Pure light today. The Sun is one of the most positive cards in the deck - energy, clarity, joy. If you've been doubting, the doubt lifts. If you've been planning, today shows you the right direction. Get outside. Celebrate something small. The card asks you to receive what's already abundant in your life.",
  },
  {
    roman: "XII",
    name: "The Hanged Man",
    image: CardTheHangedManXiiImg,
    description:
      "The Hanged Man is the card of intentional pause. You can't move forward today by pushing harder - you have to step back and see the situation from a different angle. Surrender isn't defeat. It's the moment you stop forcing what isn't working and let the next direction reveal itself.",
  },
  {
    roman: "XVI",
    name: "The Tower",
    image: CardTheTowerXviImg,
    description:
      "The Tower is rarely subtle. Something that was built on shaky ground is shaking now - a job, a relationship, a belief. It feels destructive in the moment, but the destruction is clearing space. What falls today wasn't going to hold anyway. Trust the upheaval; what comes next is more honest.",
  },
  {
    roman: "XXI",
    name: "The World",
    image: CardTheWorldXxiImg,
    description:
      "You've arrived somewhere. The World marks the end of a cycle - a project finished, a chapter closed, a version of yourself fully realized. Pause to acknowledge it. Don't rush into the next thing. This integration is part of the work, and the next cycle won't begin properly until you've honored what just ended.",
  },
  {
    roman: "II",
    name: "The High Priestess",
    image: CardTheHighPriestessIiImg,
    description:
      "There's something you already know but haven't said out loud. The High Priestess sits at the threshold of conscious and unconscious - and today she's pointing at what you've been avoiding. Get quiet. Don't ask anyone else for their take. The answer is in you, and it's been waiting for the noise to settle so you can hear it.",
  },
]

export const findTarotCard = (name: string | null | undefined): TarotCard | undefined =>
  name ? TAROT_CARDS.find((card) => card.name === name) : undefined
